import React, { useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Profile.css";
import { MyContext } from "../Context/MyContext";
import { ProfilePageSkeleton } from "../Components/Skeleton/Skeleton";
import PageLoadGate from "../Components/Skeleton/PageLoadGate";
import { resolveImage } from "../../Utils/imageUtils";
import { authApi, listingApi, protectionOrderApi, testDriveApi, getAccessToken } from "../../services/api";
import CarCard from "../Components/CarCard";
import CallModal from "../Components/CallModal";
import { useToast } from "../Components/Toast/ToastProvider";

const defaultAvatar = "https://i.pravatar.cc/150?img=3";

export default function Profile() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    fuel_type,
    profile,
    currentUser,
    refreshCurrentUser,
    userLoading,
    wishlistCars,
    getContactsForCar,
    warranty_page,
    brands,
    model,
  } = useContext(MyContext);

  const sections = profile?.sections || {};
  const profileFields = profile?.profileFields || [];
  const passwordSettings = profile?.passwordSettings || {
    requirements: [],
    securityTips: [],
  };
  const kycForm = profile?.kycForm || {};

  const [activeTab, setActiveTab] = useState(profile?.tabs?.[0]?.id || "profile");
  const [myCars, setMyCars] = useState([]);
  const [carsLoading, setCarsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [emailChanged, setEmailChanged] = useState(false);
  const [photo, setPhoto] = useState(defaultAvatar);
  const [focusedField, setFocusedField] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [kycFiles, setKycFiles] = useState({ idProof: "", idProofType: "", addressProof: "", addressProofType: "" });
  const [kycFileData, setKycFileData] = useState({ idProof: "", addressProof: "" });
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("Weak");
  const [wishlistPartners, setWishlistPartners] = useState([]);
  const [showWishlistCallModal, setShowWishlistCallModal] = useState(false);
  const [protectionOrders, setProtectionOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [testDrives, setTestDrives] = useState([]);
  const [testDrivesLoading, setTestDrivesLoading] = useState(true);

  const ordersConfig = warranty_page?.profile || sections.orders || {};
  const testDrivesConfig = sections.testDrives || {};
  const orderStatusLabels = warranty_page?.order?.statusLabels || {};
  const orderLifecycle = warranty_page?.order?.lifecycle || [];

  const loadMyCars = useCallback(async () => {
    if (!getAccessToken()) {
      setMyCars([]);
      setCarsLoading(false);
      return;
    }
    setCarsLoading(true);
    try {
      const res = await listingApi.mine();
      setMyCars(res.data || []);
    } catch {
      setMyCars([]);
    } finally {
      setCarsLoading(false);
    }
  }, []);

  const loadProtectionOrders = useCallback(async () => {
    if (!getAccessToken()) {
      setProtectionOrders([]);
      setOrdersLoading(false);
      return;
    }
    setOrdersLoading(true);
    try {
      const res = await protectionOrderApi.mine();
      setProtectionOrders(res.data || []);
    } catch {
      setProtectionOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const formatTestDriveCarTitle = useCallback(
    (car) => {
      if (!car || typeof car !== "object") return "Vehicle";
      const brandName =
        car.brand?.name || brands.find((b) => b.id === (car.brand?.legacyId ?? car.brand_id))?.name || "";
      const modelName =
        car.model?.name || model.find((m) => m.id === (car.model?.legacyId ?? car.model_id))?.name || "";
      const title = `${car.year || ""} ${brandName} ${modelName}`.trim();
      return title || "Vehicle";
    },
    [brands, model]
  );

  const loadTestDrives = useCallback(async () => {
    if (!getAccessToken()) {
      setTestDrives([]);
      setTestDrivesLoading(false);
      return;
    }
    setTestDrivesLoading(true);
    try {
      const res = await testDriveApi.mine();
      const rows = (res.data || []).map((booking) => {
        const car = booking.car;
        const carId = car?.legacyId ?? car?.id ?? booking.car;
        return {
          id: booking._id || booking.id,
          carId,
          carTitle: formatTestDriveCarTitle(car),
          status: booking.status,
          preferredDate: booking.preferredDate,
          preferredTime: booking.preferredTime,
          location: booking.location,
          partnerName: booking.partner?.name || "",
          createdAt: booking.createdAt,
        };
      });
      setTestDrives(rows);
    } catch {
      setTestDrives([]);
    } finally {
      setTestDrivesLoading(false);
    }
  }, [formatTestDriveCarTitle]);

  useEffect(() => {
    if (!getAccessToken()) {
      navigate("/login", { replace: true });
      return;
    }
    loadMyCars();
    loadProtectionOrders();
    loadTestDrives();
  }, [navigate, loadMyCars, loadProtectionOrders, loadTestDrives]);

  useEffect(() => {
    if (!userLoading && getAccessToken() && !currentUser) {
      navigate("/login", { replace: true });
    }
  }, [userLoading, currentUser, navigate]);

  useEffect(() => {
    if (!currentUser) return;
    setName(currentUser.name || "");
    setEmail(currentUser.email || "");
    setPhone(currentUser.phone || "");
    setPhoto(currentUser.photo || defaultAvatar);
    setEmailChanged(false);
    if (currentUser.kyc) {
      setKycFiles({
        idProof: currentUser.kyc.idProof?.fileName || "",
        idProofType: currentUser.kyc.idProof?.documentType || "",
        addressProof: currentUser.kyc.addressProof?.fileName || "",
        addressProofType: currentUser.kyc.addressProof?.documentType || "",
      });
    }
  }, [currentUser]);

  const displayRole =
    currentUser?.role === "admin"
      ? "Administrator"
      : currentUser?.role
        ? currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1)
        : "User";

  const calculateStrength = (password) => {
    let score = 0;
    if (!password) return "Weak";
    if (password.length >= 8) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[\W_]/.test(password)) score += 1;
    if (score <= 1) return "Weak";
    if (score === 2 || score === 3) return "Medium";
    return "Strong";
  };

  const getFuelName = (fuelTypeId) =>
    fuel_type.find((f) => f.id === fuelTypeId)?.name || "";

  const getCarDetailId = (car) => car.id ?? car.legacyId ?? car._id;

  const handleViewCar = (car) => {
    const carId = getCarDetailId(car);
    if (!carId) return;
    navigate(`/car/${carId}`, { state: { previewCar: car } });
  };

  const handleEditCar = (car) => {
    const carId = car._id ?? car.id;
    if (!carId) return;
    navigate(`/upload-car/${carId}`, { state: { editCar: car } });
  };

  const handleDelete = async (car) => {
    const id = car._id ?? car.id;
    if (!id) return;
    if (!window.confirm("Delete this listing?")) return;
    try {
      await listingApi.remove(id);
      showToast({ type: "success", title: "Deleted", message: "Listing removed" });
      window.dispatchEvent(new CustomEvent("carsinfinite:bootstrap-reload"));
      await loadMyCars();
    } catch (err) {
      showToast({ type: "error", title: "Error", message: err.message || "Delete failed" });
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await authApi.updateProfile({
        name,
        email,
        phone,
        photo,
      });
      await refreshCurrentUser();
      setEmailChanged(false);
      showToast({
        type: "success",
        title: "Profile updated",
        message: "Your profile has been saved",
      });
      if (res.user?.photo) setPhoto(res.user.photo);
    } catch (err) {
      showToast({ type: "error", title: "Save failed", message: err.message || "Could not save" });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (newPassword !== confirmPassword) {
      showToast({ type: "error", title: "Error", message: "Passwords do not match" });
      return;
    }
    setSaving(true);
    try {
      await authApi.changePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      showToast({ type: "success", title: "Password updated", message: "Your password has been changed" });
    } catch (err) {
      showToast({ type: "error", title: "Error", message: err.message || "Could not update password" });
    } finally {
      setSaving(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPhoto(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleFieldChange = (fieldName) => (e) => {
    const value = e.target.value;
    if (fieldName === "name") setName(value);
    else if (fieldName === "email") {
      setEmail(value);
      setEmailChanged(value !== (currentUser?.email || ""));
    } else if (fieldName === "phone") setPhone(value);
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setKycFiles((prev) => ({ ...prev, [type]: file.name }));
      setKycFileData((prev) => ({ ...prev, [type]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleFileTypeChange = (key, type) => {
    setKycFiles((prev) => ({ ...prev, [`${key}Type`]: type }));
  };

  const handleSendOtp = () => {
    setOtpSent(true);
    setTimeout(() => alert("OTP sent to your email!"), 500);
  };

  const handleSubmitKyc = async () => {
    if (!kycFiles.idProofType || !kycFiles.addressProofType) {
      showToast({
        type: "error",
        title: "Missing document type",
        message: "Select a document type for both ID and address proof",
      });
      return;
    }

    const hasId = kycFileData.idProof || currentUser?.kyc?.idProof?.url;
    const hasAddress = kycFileData.addressProof || currentUser?.kyc?.addressProof?.url;
    if (!hasId || !hasAddress) {
      showToast({
        type: "error",
        title: "Missing documents",
        message: "Upload both government ID and address proof",
      });
      return;
    }

    setSaving(true);
    try {
      await authApi.submitKyc({
        idProof: kycFileData.idProof || undefined,
        idProofType: kycFiles.idProofType,
        idProofFileName: kycFiles.idProof,
        addressProof: kycFileData.addressProof || undefined,
        addressProofType: kycFiles.addressProofType,
        addressProofFileName: kycFiles.addressProof,
      });
      await refreshCurrentUser();
      setKycFileData({ idProof: "", addressProof: "" });
      showToast({
        type: "success",
        title: "KYC submitted",
        message: "Your documents have been submitted for review",
      });
    } catch (err) {
      showToast({
        type: "error",
        title: "Submission failed",
        message: err.message || "Could not submit KYC documents",
      });
    } finally {
      setSaving(false);
    }
  };

  const isFieldEditable = (field) =>
    Boolean(field.editable) || (!field.readOnly && !field.disabled);

  const getFieldValue = (fieldName) => {
    if (fieldName === "email") return email;
    if (fieldName === "name") return name;
    if (fieldName === "phone") return phone;
    return currentUser?.[fieldName] ?? "";
  };

  const renderProfileField = (field) => {
    const editable = isFieldEditable(field);

    return (
      <div
        className={`form-group ${focusedField === field.name ? "focused" : ""} ${field.name === "email" && emailChanged ? "changed" : ""}`}
        key={field.name}
      >
        <div className="input-container">
          <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d={field.icon} />
          </svg>
          <input
            type={field.type || "text"}
            value={getFieldValue(field.name)}
            readOnly={!editable && field.readOnly}
            disabled={!editable && field.disabled}
            onChange={editable ? handleFieldChange(field.name) : undefined}
            onFocus={() => setFocusedField(field.name)}
            onBlur={() => setFocusedField(null)}
          />
          <label className="floating-label">{field.label}</label>
        </div>
        {field.name === "phone" && currentUser?.kyc?.status === "verified" && (
          <div className="verified-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>{sections.profile?.verifiedLabel}</span>
          </div>
        )}
      </div>
    );
  };

  const halfRows = [];
  for (let i = 0; i < profileFields.length; i += 2) {
    halfRows.push(profileFields.slice(i, i + 2));
  }

  const passwordReqs = passwordSettings.requirements.map((req) => ({
    ...req,
    met:
      req.label.includes("8")
        ? newPassword.length >= 8
        : req.label.includes("number")
          ? /\d/.test(newPassword)
          : req.label.includes("uppercase")
            ? /[A-Z]/.test(newPassword)
            : req.label.includes("special")
              ? /[\W_]/.test(newPassword)
              : req.met,
  }));

  const profileReady = Boolean(currentUser) || (!userLoading && !getAccessToken());

  if (!profileReady) {
    return <ProfilePageSkeleton />;
  }

  return (
    <PageLoadGate skeleton={<ProfilePageSkeleton />} delay={380}>
    <div className="profile-container">
      <aside className="profile-sidebar">
        <div className="sidebar-header">
          <div className="user-card">
            <div className="user-avatar-wrapper">
              <div className="user-avatar-large">
                <img src={resolveImage(photo, defaultAvatar)} alt={name || "User"} />
                <div className="avatar-overlay">
                  <label className="change-photo-btn">
                    <input type="file" onChange={handleImageChange} hidden accept="image/*" />
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </label>
                </div>
              </div>
            </div>

            <div className="user-info">
              <h3 className="user-name">{name || "User"}</h3>
              <p className="user-email">{email}</p>
              <div className="user-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                <span>{displayRole}</span>
              </div>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {profile?.tabs?.map((tab) => (
            <button
              key={tab.id}
              className={`nav-item ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d={tab.icon} />
              </svg>
              <span>{tab.label}</span>
              {activeTab === tab.id && <div className="nav-indicator"></div>}
            </button>
          ))}
        </nav>
      </aside>

      <main className="profile-main">
        {activeTab === "profile" && (
          <div className="content-card">
            <div className="card-header">
              <div className="header-badge">{sections.profile.badge}</div>
              <h2>{sections.profile.title}</h2>
              <p>{sections.profile.subtitle}</p>
            </div>

            <div className="profile-layout">
              <div className="profile-form-section">
                <form className="premium-form">
                  {halfRows.map((row, idx) => (
                    <div className="form-row" key={idx}>
                      {row.map(renderProfileField)}
                    </div>
                  ))}

                  {emailChanged && (
                    <div className="otp-section">
                      <div className="otp-header">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M12 15v2m-6-4h12a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2zm10-4a4 4 0 0 0-8 0v4h8V7z" />
                        </svg>
                        <span>{sections.profile.otp.header}</span>
                      </div>
                      <div className="otp-container">
                        <div className="otp-input-group">
                          <input
                            type="text"
                            placeholder={sections.profile.otp.placeholder}
                            maxLength="6"
                            value={otpValue}
                            onChange={(e) => setOtpValue(e.target.value)}
                            className="otp-input"
                          />
                          <button
                            type="button"
                            className={`otp-button ${otpSent ? "sent" : ""}`}
                            onClick={handleSendOtp}
                            disabled={otpSent}
                          >
                            {otpSent ? sections.profile.otp.sentLabel : sections.profile.otp.sendLabel}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <button type="button" className="save-button" onClick={handleSaveProfile} disabled={saving}>
                    <span>{saving ? "Saving…" : sections.profile?.saveButton}</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {activeTab === "cars" && (
          <div className="content-card">
            <div className="card-header">
              <div className="header-badge">{sections.cars.badge}</div>
              <h2>{sections.cars.title}</h2>
              <p>{sections.cars.subtitle}</p>
            </div>

            {carsLoading ? (
              <div className="admin-loading" style={{ padding: "2rem" }}>Loading your cars…</div>
            ) : myCars.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon-wrapper">
                  <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.8 17 8 17 8h-2c0 1.5.5 3 1.5 4.1M3 17h-2c-.6 0-1-.4-1-1v-3c0-.9.7-1.7 1.5-1.9C3.3 10.8 5 8 5 8h2c0 1.5-.5 3-1.5 4.1" />
                    <circle cx="7" cy="17" r="2" />
                    <circle cx="17" cy="17" r="2" />
                  </svg>
                </div>
                <h3>{sections.cars.emptyState.title}</h3>
                <p>{sections.cars.emptyState.description}</p>
                <button
                  className="add-button"
                  onClick={() => navigate(sections.cars.emptyState.buttonPath)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 5v14m-7-7h14" />
                  </svg>
                  <span>{sections.cars.emptyState.buttonLabel}</span>
                </button>
              </div>
            ) : (
              <div className="cars-grid">
                {myCars.map((car) => (
                  <div key={car._id || car.id} className="car-card">
                    <div className="car-image-container">
                      <img src={resolveImage(car.images?.[0])} alt={`${car.year} car`} className="car-image" />
                      <div className="car-badges">
                        <span className={`status-badge ${car.status}`}>
                          {sections.cars?.statusLabels?.[car.status] || car.status}
                        </span>
                      </div>
                      <div className="car-overlay">
                        <button
                          type="button"
                          className="quick-view"
                          onClick={() => handleViewCar(car)}
                        >
                          {sections.cars.quickViewLabel}
                        </button>
                      </div>
                    </div>

                    <div className="car-details">
                      <div className="car-header">
                        <h3 className="car-price">₹ {(car.price ?? 0).toLocaleString()}</h3>
                        <div className="car-actions">
                          <button type="button" onClick={() => handleEditCar(car)} className="edit-button">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                              <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                            </svg>
                          </button>
                          <button type="button" onClick={() => handleDelete(car)} className="delete-button">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0h10" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="car-specs">
                        <span>{car.year}</span>
                        <span>•</span>
                        <span>{(car.km_driven ?? 0).toLocaleString()} km</span>
                        <span>•</span>
                        <span>{getFuelName(car.fuel_type_id)}</span>
                      </div>
                      <div className="car-footer">
                        <div className="car-location">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          <span>{car.location}</span>
                        </div>
                        <div className="view-count">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          <span>
                            {car.view_count} {sections.cars.viewsSuffix}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "wishlist" && (
          <div className="content-card">
            <div className="card-header">
              <div className="header-badge">{sections.wishlist?.badge}</div>
              <h2>{sections.wishlist?.title}</h2>
              <p>{sections.wishlist?.subtitle}</p>
            </div>

            {wishlistCars.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">❤️</div>
                <h3>{sections.wishlist?.emptyState?.title}</h3>
                <p>{sections.wishlist?.emptyState?.description}</p>
                <button
                  className="add-button"
                  onClick={() => navigate(sections.wishlist?.emptyState?.buttonPath || "/cars")}
                >
                  <span>{sections.wishlist?.emptyState?.buttonLabel}</span>
                </button>
              </div>
            ) : (
              <div className="wishlist-grid">
                {wishlistCars.map((car) => (
                  <CarCard
                    key={car.id}
                    car={car}
                    onCall={(c) => {
                      setWishlistPartners(getContactsForCar(c.sub_register_id));
                      setShowWishlistCallModal(true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "testDrives" && (
          <div className="content-card">
            <div className="card-header">
              <div className="header-badge">{testDrivesConfig.badge}</div>
              <h2>{testDrivesConfig.title}</h2>
              <p>{testDrivesConfig.subtitle}</p>
            </div>

            {testDrivesLoading ? (
              <p className="orders-loading">Loading test drives...</p>
            ) : testDrives.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🚗</div>
                <h3>{testDrivesConfig.emptyState?.title}</h3>
                <p>{testDrivesConfig.emptyState?.description}</p>
                <button
                  className="add-button"
                  onClick={() => navigate(testDrivesConfig.emptyState?.buttonPath || "/cars")}
                >
                  <span>{testDrivesConfig.emptyState?.buttonLabel}</span>
                </button>
              </div>
            ) : (
              <div className="protection-orders-list">
                {testDrives.map((booking) => (
                  <article key={booking.id} className="protection-order-card test-drive-card">
                    <div className="protection-order-head">
                      <div>
                        <span className="order-label">{testDrivesConfig.carLabel}</span>
                        <strong>{booking.carTitle}</strong>
                      </div>
                      <span className={`order-status status-${booking.status}`}>
                        {testDrivesConfig.statusLabels?.[booking.status] || booking.status}
                      </span>
                    </div>
                    <div className="protection-order-meta">
                      <div>
                        <span>{testDrivesConfig.dateLabel}</span>
                        <strong>{booking.preferredDate}</strong>
                      </div>
                      <div>
                        <span>{testDrivesConfig.timeLabel}</span>
                        <strong>{booking.preferredTime}</strong>
                      </div>
                      {booking.location && (
                        <div>
                          <span>{testDrivesConfig.locationLabel}</span>
                          <strong>{booking.location}</strong>
                        </div>
                      )}
                      {booking.partnerName && (
                        <div>
                          <span>{testDrivesConfig.partnerLabel}</span>
                          <strong>{booking.partnerName}</strong>
                        </div>
                      )}
                      {booking.createdAt && (
                        <div>
                          <span>{testDrivesConfig.bookedLabel}</span>
                          <strong>{new Date(booking.createdAt).toLocaleDateString()}</strong>
                        </div>
                      )}
                    </div>
                    <div className="protection-order-actions">
                      {booking.carId && (
                        <button
                          type="button"
                          className="order-btn"
                          onClick={() => navigate(`/car/${booking.carId}`)}
                        >
                          {testDrivesConfig.viewCarLabel}
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "orders" && (
          <div className="content-card">
            <div className="card-header">
              <div className="header-badge">{ordersConfig.badge}</div>
              <h2>{ordersConfig.title}</h2>
              <p>{ordersConfig.subtitle}</p>
            </div>

            {ordersLoading ? (
              <p className="orders-loading">Loading orders...</p>
            ) : protectionOrders.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🛡️</div>
                <h3>{ordersConfig.emptyState?.title}</h3>
                <p>{ordersConfig.emptyState?.description}</p>
                <button
                  className="add-button"
                  onClick={() => navigate(ordersConfig.emptyState?.buttonPath || "/cars")}
                >
                  <span>{ordersConfig.emptyState?.buttonLabel}</span>
                </button>
              </div>
            ) : (
              <div className="protection-orders-list">
                {protectionOrders.map((order) => {
                  const lifecycleIndex = orderLifecycle.findIndex((s) => s.status === order.status);
                  return (
                    <article key={order.id} className="protection-order-card">
                      <div className="protection-order-head">
                        <div>
                          <span className="order-label">{ordersConfig.carLabel}</span>
                          <strong>{order.carTitle || `Car #${order.carId}`}</strong>
                        </div>
                        <span className={`order-status status-${order.status}`}>
                          {orderStatusLabels[order.status] || order.status}
                        </span>
                      </div>
                      <div className="protection-order-meta">
                        <div>
                          <span>{ordersConfig.planLabel}</span>
                          <strong>{order.planName}</strong>
                        </div>
                        <div>
                          <span>{ordersConfig.requestedLabel}</span>
                          <strong>{new Date(order.createdAt).toLocaleDateString()}</strong>
                        </div>
                        {order.returnDeadline && (
                          <div>
                            <span>{ordersConfig.returnDeadlineLabel}</span>
                            <strong>{new Date(order.returnDeadline).toLocaleDateString()}</strong>
                          </div>
                        )}
                      </div>
                      {orderLifecycle.length > 0 && (
                        <div className="order-lifecycle-mini">
                          {orderLifecycle.map((step, index) => (
                            <span
                              key={step.status}
                              className={`lifecycle-dot ${index <= lifecycleIndex ? "done" : ""}`}
                              title={step.label}
                            />
                          ))}
                        </div>
                      )}
                      <div className="protection-order-actions">
                        {order.carId && (
                          <button
                            type="button"
                            className="order-btn"
                            onClick={() => navigate(`/car/${order.carId}`)}
                          >
                            {ordersConfig.viewCarLabel}
                          </button>
                        )}
                        <button
                          type="button"
                          className="order-btn ghost"
                          onClick={() => navigate(ordersConfig.policyLabel ? "/warranty" : "/warranty")}
                        >
                          {ordersConfig.policyLabel || "View Policy"}
                        </button>
                        {["return_window", "delivered"].includes(order.status) && (
                          <button
                            type="button"
                            className="order-btn warn"
                            onClick={async () => {
                              try {
                                await protectionOrderApi.requestReturn(order.id);
                                showToast({
                                  type: "success",
                                  title: "Return requested",
                                  message: "Our team will review your return request.",
                                });
                                loadProtectionOrders();
                              } catch (err) {
                                showToast({
                                  type: "error",
                                  title: "Request failed",
                                  message: err.message,
                                });
                              }
                            }}
                          >
                            Request Return
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "kyc" && (
          <div className="content-card">
            <div className="card-header">
              <div className="header-badge">{sections.kyc.badge}</div>
              <h2>{sections.kyc.title}</h2>
              <p>{sections.kyc.subtitle}</p>
            </div>

            <div className="kyc-grid">
              {currentUser?.kyc?.status && currentUser.kyc.status !== "none" && (
                <div className="admin-banner admin-banner-info" style={{ gridColumn: "1 / -1", marginBottom: "1rem" }}>
                  KYC status: <strong>{currentUser.kyc.status}</strong>
                </div>
              )}
              {Object.keys(kycForm).map((key) => {
                const data = kycForm[key];
                return (
                  <div className="upload-box" key={key}>
                    <div className="upload-icon-wrapper">
                      <svg viewBox={data.icon.viewBox} fill="none" stroke="currentColor">
                        {data.icon.paths.map((p, idx) =>
                          p.type === "path" ? (
                            <path key={idx} d={p.d} />
                          ) : (
                            <polyline key={idx} points={p.points} />
                          )
                        )}
                      </svg>
                    </div>

                    <h4>{data.label}</h4>

                    <select
                      className="kyc-document-type"
                      value={kycFiles[`${key}Type`] || ""}
                      onChange={(e) => handleFileTypeChange(key, e.target.value)}
                    >
                      <option value="">{sections.kyc.selectTypeLabel}</option>
                      {data.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    <div className="file-upload-area">
                      <label className="file-upload-button">
                        <input
                          type="file"
                          onChange={(e) => handleFileChange(e, key)}
                          hidden
                          accept="image/*,.pdf"
                        />
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <span>{kycFiles[key] || sections.kyc.chooseFileLabel}</span>
                      </label>

                      {kycFiles[key] && (
                        <div className="file-status">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span>{sections.kyc.uploadSuccessLabel}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="kyc-submit-button"
              onClick={handleSubmitKyc}
              disabled={saving || currentUser?.kyc?.status === "verified"}
            >
              <span>{sections.kyc.submitLabel}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </button>
          </div>
        )}

        {activeTab === "password" && (
          <div className="content-card">
            <div className="card-header">
              <div className="header-badge">{sections.password.badge}</div>
              <h2>{sections.password.title}</h2>
              <p>{sections.password.subtitle}</p>
            </div>

            <div className="security-layout">
              <div className="password-form-section">
                <form className="premium-form">
                  <div className="password-strength">
                    <div className="strength-meter">
                      <div
                        className={`strength-bar ${passwordStrength.toLowerCase()}`}
                        style={{
                          width:
                            passwordStrength === "Weak"
                              ? "33%"
                              : passwordStrength === "Medium"
                              ? "66%"
                              : "100%",
                        }}
                      ></div>
                    </div>
                    <div className="strength-labels">
                      {sections.password?.strengthLabels?.map((label) => (
                        <span key={label} className={passwordStrength === label ? "active" : ""}>
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  {["current", "new", "confirm"].map((field) => (
                    <div className={`form-group ${focusedField === field ? "focused" : ""}`} key={field}>
                      <div className="input-container">
                        <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        <input
                          type="password"
                          value={
                            field === "current"
                              ? currentPassword
                              : field === "new"
                                ? newPassword
                                : confirmPassword
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            if (field === "current") setCurrentPassword(val);
                            if (field === "new") {
                              setNewPassword(val);
                              setPasswordStrength(calculateStrength(val));
                            }
                            if (field === "confirm") setConfirmPassword(val);
                          }}
                          onFocus={() => setFocusedField(field)}
                          onBlur={() => setFocusedField(null)}
                        />
                        <label className="floating-label">
                          {sections.password?.fieldLabels?.[field]}
                        </label>
                      </div>

                      {field === "new" && (
                        <div className="password-requirements">
                          {passwordReqs.map((req, idx) => (
                            <span key={idx} className={`requirement ${req.met ? "met" : ""}`}>
                              {req.met ? "✓ " : "✗ "} {req.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    className="update-password-button"
                    onClick={handleUpdatePassword}
                    disabled={saving}
                  >
                    <span>{saving ? "Updating…" : sections.password?.updateButton}</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </form>
              </div>

              <div className="security-tips-section">
                <div className="security-card">
                  <div className="security-header">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    <h4>{sections.password.securityTipsTitle}</h4>
                  </div>
                  <ul className="tips-list">
                    {passwordSettings.securityTips.map((tip, idx) => (
                      <li key={idx}>{tip}</li>
                    ))}
                  </ul>
                </div>

                <div className="two-factor-card">
                  <div className="two-factor-header">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                    <div>
                      <h4>{sections.password.twoFactor.title}</h4>
                      <p>{sections.password.twoFactor.description}</p>
                    </div>
                  </div>
                  <button type="button" className="enable-2fa">
                    {sections.password.twoFactor.buttonLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {showWishlistCallModal && (
        <CallModal
          partners={wishlistPartners}
          close={() => setShowWishlistCallModal(false)}
        />
      )}
    </div>
    </PageLoadGate>
  );
}